import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useOktaAuth } from '@okta/okta-react'
import { useCompleteFirmChangeMutation, useStartFirmChangeMutation } from '../../api/special'
import FirmSelector from './FirmSelector'
import UtilityProgress from './UtilityProgress'

export const SummitUtilityProviderContext = createContext({})

export const SummitUtilityProvider = ({
  currentUser,
  children
}) => {
  const { mutateAsync: start } = useStartFirmChangeMutation()
  const { mutateAsync: complete } = useCompleteFirmChangeMutation()
  const oktaAuth = useOktaAuth()
  const [inProcess, setInProcess] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const providerValue = useMemo(() => {
    console.debug('utility provider', currentUser)

    if (currentUser?.userType === 'Summit') {
      return {
        start,
        complete,
        oktaAuth,
        enabled: true
      }
    }

    return {
      enabled: false
    }
  }, [currentUser, start, complete, oktaAuth])

  const handleOpen = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setModalOpen(false)
  }, [])

  const handleConfirm = useCallback(async (toFirmId) => {
    setModalOpen(false)
    setInProcess(true)
    try {
      const { firmChangeId } = await providerValue.start({ toFirmId })
      const result = await providerValue.complete({ firmChangeId })
      if (result) {
        await providerValue.oktaAuth.oktaAuth.token.getWithRedirect()
      } else {
        setInProcess(false)
      }
    } catch (err) {
      console.error(err, err?.summary)
      setInProcess(false)
    }
  }, [providerValue, setInProcess])

  const hotKeyHandler = useCallback((evt) => {
    if (!providerValue.enabled) {
      return false
    }
    if (evt.ctrlKey && evt.shiftKey && (evt.key === 'F' || evt.code === 'KeyF')) {
      evt.preventDefault()
      handleOpen()
    }
  }, [providerValue, handleOpen])

  useEffect(() => {
    if (providerValue.enabled) {
      window.addEventListener('keydown', hotKeyHandler)
    }
    return () => {
      window.removeEventListener('keydown', hotKeyHandler)
    }
  }, [providerValue, hotKeyHandler])

  return (
    <SummitUtilityProviderContext.Provider value={providerValue}>
      {inProcess ? (<UtilityProgress message='Please wait, changing instance...' />) : children}
      <FirmSelector
        open={modalOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        enabled={currentUser?.userType === 'Summit'}
      />
    </SummitUtilityProviderContext.Provider>
  )
}

SummitUtilityProvider.propTypes = {
  currentUser: PropTypes.shape({
    userType: PropTypes.string
  }),
  children: PropTypes.node
}

export default SummitUtilityProvider
